import React, { useEffect, useMemo, useState } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import Table from 'react-bootstrap/Table';

import { useIsAdmin } from '../auth/useIsAdmin';
import { useAdminSession } from '../auth/useAdminSession';
import { useTopLayers } from '../map-gl/useTopLayers';
import {
  OCR_GOLD_AVAILABLE, OCR_GOLD_RESTRICTED, OCR_GOLD_INTERACTIVE, OCR_GOLD_ORDER, OCR_GOLD_VECTOR_SRC,
  buildOcrGoldSource, ocrGoldConfidenceShown, ocrGoldLayer,
} from './ocrGoldStyle';

const WAMEX = 'https://wamex.dmp.wa.gov.au/Wamex/Search/ReportDetails?ANumber=';
/** Click tolerance, in screen pixels either side of the cursor. */
const HIT_SLOP_PX = 4;

/**
 * OCR gold values (local only) — see ocrGoldStyle.js for what and why.
 *
 * Owns its click handler, like the prospect overlay, so switching the layer
 * off takes the handler with it.
 */
export function OcrGoldOverlay({ overlayVisibility }) {
  // Internal working material, like the prospect reports: admin only.
  const isAdmin = useIsAdmin();
  // Through the API (a deployed build): no tile request before the session.
  const session = useAdminSession(OCR_GOLD_RESTRICTED);
  const on = !!overlayVisibility?.ocrGold && OCR_GOLD_AVAILABLE && isAdmin
    && (!OCR_GOLD_RESTRICTED || session);
  const { current: map } = useMap();
  const [picked, setPicked] = useState(null);
  const source = useMemo(() => (OCR_GOLD_AVAILABLE ? buildOcrGoldSource() : null), []);

  // Reduced to a string before memoising: overlayVisibility is a fresh object
  // on every sidebar change, and the layer should only rebuild when the
  // confidence selection itself moves.
  const confKey = ocrGoldConfidenceShown(overlayVisibility).join(',');
  const layer = useMemo(() => ocrGoldLayer(confKey ? confKey.split(',') : []), [confKey]);

  useEffect(() => {
    if (!map || !on) return undefined;
    const onClick = (e) => {
      const present = OCR_GOLD_INTERACTIVE.filter((id) => map.getLayer(id));
      if (!present.length) return;
      let hits = map.queryRenderedFeatures(e.point, { layers: present });
      if (!hits.length) {
        hits = map.queryRenderedFeatures([
          [e.point.x - HIT_SLOP_PX, e.point.y - HIT_SLOP_PX],
          [e.point.x + HIT_SLOP_PX, e.point.y + HIT_SLOP_PX],
        ], { layers: present });
      }
      if (!hits.length) return;
      // A feature straddling a tile edge comes back once per tile.
      const seen = new Map();
      hits.forEach((h) => {
        const p = h.properties || {};
        const key = `${p.anumber}/${p.page}/${p.sample_raw}/${p.au_raw}/${p.from_m}`;
        if (!seen.has(key)) seen.set(key, p);
      });
      const rows = [...seen.values()]
        .sort((a, b) => (Number(b.au_ppb) || -1) - (Number(a.au_ppb) || -1));
      setPicked({ lngLat: e.lngLat, rows, i: 0 });
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [map, on]);

  useEffect(() => { if (!on) setPicked(null); }, [on]);
  // A confidence switched off takes its reads out of an open popup too.
  useEffect(() => {
    if (!picked) return;
    const shown = new Set(confKey.split(','));
    const left = picked.rows.filter((r) => shown.has(r.confidence));
    if (left.length === picked.rows.length) return;
    setPicked(left.length ? { ...picked, rows: left, i: 0 } : null);
  }, [confKey, picked]);

  // Above the soil and drill tiles it is compared against, below the prospect
  // reports (rank 20), which are the conclusions drawn over this evidence.
  useTopLayers('ocr-gold', 15, OCR_GOLD_ORDER, map, on);

  if (!on) return null;

  return (
    <>
      <Source id={OCR_GOLD_VECTOR_SRC} {...source}>
        <Layer {...layer} />
      </Source>
      {picked && (
        <Popup longitude={picked.lngLat.lng} latitude={picked.lngLat.lat}
               anchor="bottom" closeOnClick={false}
               onClose={() => setPicked(null)}>
          <OcrGoldPopupTable
            p={picked.rows[picked.i]}
            i={picked.i}
            n={picked.rows.length}
            onStep={(d) => setPicked((prev) => ({
              ...prev, i: (prev.i + d + prev.rows.length) % prev.rows.length,
            }))}
          />
        </Popup>
      )}
    </>
  );
}

const hasValue = (v) => v !== undefined && v !== null && v !== '';

const fmtPpb = (p) => {
  if (!hasValue(p.au_ppb)) return `no ppb (printed “${p.au_raw ?? ''}”)`;
  const n = Number(p.au_ppb);
  return p.value_state === 'below_dl' ? `< ${n}` : `${n}`;
};

const DATUM_LABEL = {
  printed_amg: 'AMG (printed)',
  assumed_amg: 'AMG (assumed)',
};

/**
 * Laid out like the soil sample popup (createSingleAssayPopupTable): a striped,
 * bordered label/value table, sample id first, then Au, type and A-number.
 */
function OcrGoldPopupTable({ p, i, n, onStep }) {
  return (
    <div>
      {n > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          <button type="button" className="btn btn-light btn-sm py-0" onClick={() => onStep(-1)} aria-label="Previous read">‹</button>
          <span>{i + 1} of {n} reads here</span>
          <button type="button" className="btn btn-light btn-sm py-0" onClick={() => onStep(1)} aria-label="Next read">›</button>
        </div>
      )}
      <Table striped bordered className="mb-1">
        <tbody>
          <tr>
            <td>Sample ID:</td>
            <td>{p.sample_raw || '—'}</td>
          </tr>
          <tr>
            <td>Au PPB:</td>
            <td>
              {fmtPpb(p)}
              {hasValue(p.au_ppb) && p.unit_raw && p.unit_raw.toLowerCase() !== 'ppb' && (
                <span style={{ color: '#6b6b6b' }}> (printed {p.au_raw} {p.unit_raw})</span>
              )}
            </td>
          </tr>
          <tr>
            <td>Sample Type:</td>
            <td>{p.medium_raw || '—'}</td>
          </tr>
          {(hasValue(p.from_m) || hasValue(p.to_m)) && (
            <tr>
              <td>Depth:</td>
              <td>{p.from_m ?? '?'}–{p.to_m ?? '?'} m</td>
            </tr>
          )}
          <tr>
            <td>A Number:</td>
            <td>
              <a target="_blank" rel="noreferrer" href={`${WAMEX}${p.anumber}`}>{p.anumber}</a>
              {hasValue(p.page) && <span style={{ color: '#6b6b6b' }}> · p.{p.page}</span>}
            </td>
          </tr>
          {hasValue(p.year_est) && (
            <tr>
              <td>Year:</td>
              <td>~{p.year_est}</td>
            </tr>
          )}
          <tr>
            <td>Read:</td>
            <td>
              {p.confidence ? `${p.confidence} confidence` : '—'}
              {p.confirmed === true ? ', second read agrees' : ', unconfirmed'}
            </td>
          </tr>
          <tr>
            <td>Position:</td>
            <td>
              {DATUM_LABEL[p.datum_basis] || p.datum_basis || '—'}
              {Number(p.pos_check_km) > 0 && `, ${Number(p.pos_check_km)} km outside report footprint`}
            </td>
          </tr>
          {hasValue(p.in_gswa) && (
            <tr>
              <td>In GSWA:</td>
              <td>{p.in_gswa === true ? 'yes' : 'no'}</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}
